/**
 * Semaines et jours du planning v2 (docs/ux-v2/SPEC.md PL1, PL2). Semaines ISO (lundi → dimanche), comme
 * screens/Classes (getISOWeek / getISOWeekYear : 29-31/12/2025 = semaine 1 de 2026).
 */
import {
  addDays,
  addWeeks,
  differenceInCalendarDays,
  format,
  getISOWeek,
  getISOWeekYear,
  isSameDay,
  setISOWeek,
  startOfDay,
  startOfISOWeek,
  subDays,
} from 'date-fns';
import { fr } from 'date-fns/locale';

export const weekStartOf = (date: Date) => startOfISOWeek(date);

export function isoWeekOf(date: Date): { week: number; year: number } {
  return { week: getISOWeek(date), year: getISOWeekYear(date) };
}

export function weekDays(weekStart: Date): Date[] {
  return Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
}

/** Semaine précédente impossible depuis la semaine courante (= Filters/index.tsx isCurrentWeek). */
export function canGoToPreviousWeek(weekStart: Date, today: Date): boolean {
  return weekStartOf(weekStart).getTime() > weekStartOf(today).getTime();
}

/**
 * Lundi de la semaine ISO `week` demandée par une route (`studio/:id/week/:n`) : semaine de l'année ISO en cours,
 * comme screens/Classes ; si elle est déjà passée, celle de l'année suivante (lien de fin d'année vers janvier).
 */
export function weekStartFromNumber(week: number, today: Date): Date {
  const isoYear = getISOWeekYear(today);
  const candidate = startOfISOWeek(setISOWeek(new Date(isoYear, 0, 4), week));
  if (candidate.getTime() >= weekStartOf(today).getTime()) return candidate;
  return startOfISOWeek(setISOWeek(new Date(isoYear + 1, 0, 4), week));
}

/**
 * « 5 oct. — 11 oct. » ; hors de l'année en cours, l'année une seule fois, en fin de plage : « 28 déc. — 3 janv.
 * 2027 », « 4 janv. — 10 janv. 2027 » (revue du 05/10 : « 28 déc. 2026 — 3 janv. 2027 » dépassait la largeur réservée
 * à la plage et les flèches bougeaient de nouveau d'une semaine à l'autre). Espaces insécables dans chaque date.
 */
export function weekRangeLabel(weekStart: Date, today: Date): string {
  const end = addDays(weekStart, 6);
  const sameYear = weekStart.getFullYear() === today.getFullYear() && end.getFullYear() === today.getFullYear();
  const day = (date: Date) => format(date, 'd\u00a0MMM', { locale: fr });
  return `${day(weekStart)} — ${day(end)}${sameYear ? '' : `\u00a0${format(end, 'yyyy')}`}`;
}

/** « LUN » (en-tête de colonne, pastille de jour). */
export const dayShort = (day: Date) => format(day, 'EEE', { locale: fr }).replace('.', '').toUpperCase();

/** « 05/10 ». */
export const dayNumber = (day: Date) => format(day, 'dd/MM');

/** « lundi 5 octobre ». */
export const dayLong = (day: Date) => format(day, 'EEEE d MMMM', { locale: fr });

/** Jour affiché par défaut en mobile : aujourd'hui dans la semaine courante, sinon le lundi (= MobileClasses). */
export function defaultSelectedDay(weekStart: Date, today: Date): Date {
  const days = weekDays(weekStart);
  return days.find((day) => isSameDay(day, today)) ?? startOfDay(weekStart);
}

/** Jour sélectionné valide pour la semaine affichée (sinon le jour par défaut). */
export function effectiveSelectedDay(selected: Date | null, weekStart: Date, today: Date): Date {
  if (selected) {
    const offset = differenceInCalendarDays(selected, weekStart);
    if (offset >= 0 && offset < 7) return startOfDay(selected);
  }
  return defaultSelectedDay(weekStart, today);
}

/**
 * Jour sélectionné après un changement de semaine : même jour de la semaine (= MobileClasses), sans revenir
 * avant aujourd'hui.
 */
export function shiftSelectedDay(selected: Date, weeks: number, today: Date): Date {
  const next = startOfDay(addWeeks(selected, weeks));
  return next.getTime() < startOfDay(today).getTime() ? startOfDay(today) : next;
}

export const isPastDay = (day: Date, today: Date) => startOfDay(day).getTime() < startOfDay(today).getTime();

/**
 * Ouverture des réservations d'un cours hors fenêtre : la fenêtre de utils/functions.ts getBookableDays va
 * d'aujourd'hui (d'hier avant midi) à + `numberOfDays` jours ; un cours du jour J entre donc dans la fenêtre le jour
 * J − numberOfDays à 12 h.
 */
export function bookingOpensAt(classStart: Date, numberOfDays: number): Date {
  const opening = subDays(startOfDay(classStart), numberOfDays);
  opening.setHours(12, 0, 0, 0);
  return opening;
}
